import React, { Component } from "react";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Edit2, Trash2, RefreshCw, X, Save, Search, DollarSign, CreditCard, CheckCircle, Clock, AlertCircle } from "lucide-react";

const PAYMENT_MODES = ["Cash", "Bank Transfer", "Cheque", "UPI", "Credit Card", "Debit Card", "NEFT", "RTGS", "IMPS", "Other"];

const MODE_COLORS = {
  "Cash":          { bg: "#ECFDF5", color: "#0B7A4C", border: "#A7F3D0" },
  "UPI":           { bg: "#EFF6FF", color: "#1E40AF", border: "#BFDBFE" },
  "Bank Transfer": { bg: "#F5F3FF", color: "#5B21B6", border: "#DDD6FE" },
  "Cheque":        { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
  "Credit Card":   { bg: "#FFF1F2", color: "#9F1239", border: "#FECDD3" },
  "Debit Card":    { bg: "#F0F9FF", color: "#0369A1", border: "#BAE6FD" },
  "NEFT":          { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0" },
  "RTGS":          { bg: "#FDF4FF", color: "#7E22CE", border: "#E9D5FF" },
  "IMPS":          { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
  "Other":         { bg: "#F9FAFB", color: "#374151", border: "#E5E7EB" },
};

const fmt = n => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(parseFloat(n) || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

/* ─── Modal ─────────────────────────────────────────────────────────────────── */
class PaymentModal extends Component {
  constructor(props) {
    super(props);
    const p = props.payment;
    this.state = {
      form: {
        booking_id:      p?.booking_id      || "",
        amount:          p?.amount          || "",
        mode_of_payment: p?.mode_of_payment || "",
        reference_no:    p?.reference_no    || "",
        payment_date:    p?.payment_date    ? String(p.payment_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        notes:           p?.notes           || "",
      },
      booking: p ? { guest_name: p.guest_name, hotel_name: p.hotel_name } : null,
      loadingBooking: false, saving: false, errors: {},
    };
    this.bookingSearchTimer = null;
  }

  validate() {
    const { form } = this.state; const errs = {};
    if (!form.booking_id)         errs.booking_id = "Booking ID is required";
    if (!form.amount || parseFloat(form.amount) <= 0) errs.amount = "Valid amount required";
    if (!form.mode_of_payment)    errs.mode_of_payment = "Select payment mode";
    return errs;
  }

  async lookupBooking(id) {
    if (!id) { this.setState({ booking: null }); return; }
    this.setState({ loadingBooking: true });
    try {
      const { data } = await api.get(`/bookings/${id}`);
      const b = data.data;
      this.setState({ booking: b ? { guest_name: b.guest_name, hotel_name: b.hotel_name, total_sale: b.total_sale, conf_no: b.conf_no } : null });
      if (!b) this.setState({ errors: { ...this.state.errors, booking_id: `Booking #${id} not found` } });
    } catch { this.setState({ booking: null, errors: { ...this.state.errors, booking_id: `Booking #${id} not found` } }); }
    finally { this.setState({ loadingBooking: false }); }
  }

  async handleSubmit() {
    const errs = this.validate();
    if (Object.keys(errs).length) { this.setState({ errors: errs }); return; }
    this.setState({ saving: true });
    try {
      if (this.props.payment) {
        await api.put(`/payments/${this.props.payment.id}`, this.state.form);
        toast.success("Payment updated!");
      } else {
        await api.post("/payments", this.state.form);
        toast.success("Payment recorded!");
      }
      this.props.onSaved();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { this.setState({ saving: false }); }
  }

  render() {
    const { onClose, payment } = this.props;
    const { form, booking, loadingBooking, saving, errors } = this.state;
    const inp = err => ({ width: "100%", background: err ? "#FFF5F5" : "#F9FAFB", border: `1.5px solid ${err ? "#FCA5A5" : "#D1D5DB"}`, borderRadius: 10, padding: "9px 12px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none" });
    const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 5 };
    const set = (k, v) => this.setState(p => ({ form: { ...p.form, [k]: v }, errors: { ...p.errors, [k]: "" } }));

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 540, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ background: "#16213E", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
              <CreditCard size={16} color="#C9A84C" /> {payment ? "Edit Payment" : "Record Payment"}
            </span>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
          </div>

          {/* Body */}
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Booking ID */}
            <div>
              <label style={lbl}>Booking ID *</label>
              <div style={{ position: "relative" }}>
                <input type="number" value={form.booking_id} placeholder="Enter booking ID (e.g. 42)"
                  onChange={e => {
                    set("booking_id", e.target.value);
                    clearTimeout(this.bookingSearchTimer);
                    this.bookingSearchTimer = setTimeout(() => this.lookupBooking(e.target.value), 600);
                  }}
                  style={{ ...inp(errors.booking_id), paddingRight: 36 }} />
                {loadingBooking && <RefreshCw size={13} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", animation: "spin 1s linear infinite", color: "#9CA3AF" }} />}
              </div>
              {errors.booking_id && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 3 }}>{errors.booking_id}</p>}
              {booking && (
                <div style={{ marginTop: 6, padding: "8px 12px", background: "#F0FDF4", border: "1px solid #A7F3D0", borderRadius: 8, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: "#0B7A4C" }}>✓ {booking.guest_name}</span>
                  {booking.hotel_name && <span style={{ color: "#6B7280" }}> — {booking.hotel_name}</span>}
                  {booking.total_sale && <span style={{ color: "#0FA968", fontWeight: 600, marginLeft: 8 }}>Total: ₹{fmt(booking.total_sale)}</span>}
                </div>
              )}
            </div>

            {/* Amount + Date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={lbl}>Amount (₹) *</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: 13, fontWeight: 600 }}>₹</span>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set("amount", e.target.value)}
                    style={{ ...inp(errors.amount), paddingLeft: 26 }} placeholder="0.00" />
                </div>
                {errors.amount && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 3 }}>{errors.amount}</p>}
              </div>
              <div>
                <label style={lbl}>Payment Date *</label>
                <input type="date" value={form.payment_date} onChange={e => set("payment_date", e.target.value)}
                  onClick={e => { try { e.target.showPicker(); } catch(_){} }}
                  style={{ ...inp(false), cursor: "pointer" }} />
              </div>
            </div>

            {/* Mode of Payment */}
            <div>
              <label style={lbl}>Mode of Payment *</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {PAYMENT_MODES.map(m => {
                  const active = form.mode_of_payment === m;
                  const clr = MODE_COLORS[m] || MODE_COLORS["Other"];
                  return (
                    <button key={m} type="button" onClick={() => set("mode_of_payment", m)}
                      style={{ padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                        border: active ? `2px solid ${clr.color}` : "1.5px solid #E5E7EB",
                        background: active ? clr.bg : "#fff", color: active ? clr.color : "#374151",
                        boxShadow: active ? "0 2px 6px rgba(0,0,0,0.1)" : "none" }}>
                      {m}
                    </button>
                  );
                })}
              </div>
              {errors.mode_of_payment && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 3 }}>{errors.mode_of_payment}</p>}
            </div>

            {/* Reference No + Notes */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={lbl}>Reference No. (optional)</label>
                <input value={form.reference_no} onChange={e => set("reference_no", e.target.value)}
                  style={inp(false)} placeholder="TXN123 / Cheque No." />
              </div>
              <div>
                <label style={lbl}>Notes (optional)</label>
                <input value={form.notes} onChange={e => set("notes", e.target.value)}
                  style={inp(false)} placeholder="Any remarks..." />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
            <button onClick={() => this.handleSubmit()} disabled={saving}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saving ? "#9CA3AF" : "#16213E", fontSize: 13, fontWeight: 700, color: "#fff", cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              {saving ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : <><Save size={14} /> {payment ? "Update" : "Record Payment"}</>}
            </button>
          </div>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
class PaymentsPage extends Component {
  constructor(props) {
    super(props);
    this.state = { payments: [], total: 0, loading: true, showModal: false, editPayment: null, search: "", filterMode: "" };
    this.fetch = this.fetch.bind(this);
  }
  componentDidMount() { this.fetch(); }

  async fetch() {
    this.setState({ loading: true });
    try {
      const { data } = await api.get("/payments", { params: { limit: 200 } });
      this.setState({ payments: data.data || [], total: data.total || 0 });
    } catch { toast.error("Failed to load payments"); }
    finally { this.setState({ loading: false }); }
  }

  async handleDelete(id) {
    if (!window.confirm("Delete this payment record?")) return;
    try { await api.delete(`/payments/${id}`); toast.success("Deleted"); this.fetch(); }
    catch (e) { toast.error(e.response?.data?.message || "Cannot delete"); }
  }

  render() {
    const { payments, loading, showModal, editPayment, search, filterMode } = this.state;
    const { user } = this.props;

    // Filters
    const filtered = payments.filter(p => {
      const q = search.toLowerCase();
      const matchSearch = !q || String(p.booking_id).includes(q) || (p.guest_name || "").toLowerCase().includes(q) || (p.hotel_name || "").toLowerCase().includes(q) || (p.reference_no || "").toLowerCase().includes(q);
      const matchMode = !filterMode || p.mode_of_payment === filterMode;
      return matchSearch && matchMode;
    });

    // Stats
    const totalAmount    = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const todayPayments  = payments.filter(p => String(p.payment_date || "").slice(0, 10) === new Date().toISOString().slice(0, 10));
    const todayAmount    = todayPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const modeBreakdown  = PAYMENT_MODES.map(m => ({ mode: m, count: payments.filter(p => p.mode_of_payment === m).length })).filter(m => m.count > 0);

    return (
      <div>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "#16213E", letterSpacing: "-0.02em" }}>
              Payment <span style={{ color: "#FF6B4A" }}>Records</span>
            </h1>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>{payments.length} records · ₹{fmt(totalAmount)} total collected</p>
          </div>
          <button onClick={() => this.setState({ showModal: true, editPayment: null })}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 22px", borderRadius: 12, border: "none", background: "#FF6B4A", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(255,107,74,0.28)" }}>
            <Plus size={16} /> Record Payment
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 20 }}>
          {[
            { label: "Total Collected", value: `₹${fmt(totalAmount)}`, sub: `${payments.length} records`, icon: "💰", bg: "#F0FDF4", border: "#A7F3D0", color: "#0B7A4C" },
            { label: "Today's Collection", value: `₹${fmt(todayAmount)}`, sub: `${todayPayments.length} payment${todayPayments.length !== 1 ? "s" : ""}`, icon: "📅", bg: "#EFF6FF", border: "#BFDBFE", color: "#1E40AF" },
            { label: "Unique Bookings", value: [...new Set(payments.map(p => p.booking_id))].length, sub: "bookings paid", icon: "📋", bg: "#F5F3FF", border: "#DDD6FE", color: "#5B21B6" },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 14, padding: "16px 20px" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} />
            <input placeholder="Search booking ID, guest, hotel..." value={search}
              onChange={e => this.setState({ search: e.target.value })}
              style={{ width: "100%", paddingLeft: 34, padding: "9px 12px 9px 34px", border: "1.5px solid #E5E7EB", borderRadius: 10, fontSize: 13, outline: "none", fontFamily: "Sora,sans-serif", boxSizing: "border-box" }} />
          </div>
          <select value={filterMode} onChange={e => this.setState({ filterMode: e.target.value })}
            style={{ height: 38, border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "0 12px", fontSize: 13, fontFamily: "Sora,sans-serif", outline: "none", background: "#fff" }}>
            <option value="">All Modes</option>
            {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {(search || filterMode) && (
            <button onClick={() => this.setState({ search: "", filterMode: "" })}
              style={{ height: 38, padding: "0 14px", border: "1px solid #E5E7EB", borderRadius: 10, background: "#F9FAFB", fontSize: 12, color: "#6B7280", cursor: "pointer" }}>
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9CA3AF" }}>
              <RefreshCw size={22} style={{ animation: "spin 1s linear infinite", marginBottom: 10 }} />
              <p>Loading payments…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#9CA3AF" }}>
              <CreditCard size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>{payments.length === 0 ? "No payments recorded yet" : "No results found"}</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    {["#", "Date", "Booking", "Guest / Hotel", "Mode", "Reference", "Amount", "Recorded By", "Actions"].map(h => (
                      <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9CA3AF", borderBottom: "1.5px solid #F3F4F6", background: "#FAFBFF", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const clr = MODE_COLORS[p.mode_of_payment] || MODE_COLORS["Other"];
                    return (
                      <tr key={p.id} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}>
                        <td style={{ padding: "12px 16px", fontSize: 11, color: "#9CA3AF", fontFamily: "JetBrains Mono,monospace", borderBottom: "1px solid #F3F4F6" }}>#{p.id}</td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{fmtDate(p.payment_date)}</td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 12, fontWeight: 700, color: "#16213E", background: "#EEF2FF", padding: "2px 8px", borderRadius: 6 }}>
                            #{p.booking_id}
                          </span>
                          {p.conf_no && <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>Conf: {p.conf_no}</div>}
                        </td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{p.guest_name || "—"}</div>
                          {p.hotel_name && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{p.hotel_name}</div>}
                        </td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: clr.bg, color: clr.color, border: `1px solid ${clr.border}`, whiteSpace: "nowrap" }}>
                            {p.mode_of_payment}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", borderBottom: "1px solid #F3F4F6" }}>
                          {p.reference_no ? <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 11 }}>{p.reference_no}</span> : <span style={{ color: "#D1D5DB" }}>—</span>}
                          {p.notes && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{p.notes}</div>}
                        </td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 14, fontWeight: 800, color: "#0B7A4C" }}>₹{fmt(p.amount)}</span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#6B7280", borderBottom: "1px solid #F3F4F6" }}>{p.recorded_by || "—"}</td>
                        <td style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => this.setState({ showModal: true, editPayment: p })}
                              style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                              <Edit2 size={12} />
                            </button>
                            <button onClick={() => this.handleDelete(p.id)}
                              style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #F5CAC5", background: "#FCEAE8", color: "#E1483A", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Total footer */}
                <tfoot>
                  <tr style={{ background: "#16213E" }}>
                    <td colSpan={6} style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#C9A84C", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {filtered.length} Record{filtered.length !== 1 ? "s" : ""}
                    </td>
                    <td style={{ padding: "12px 16px", fontFamily: "JetBrains Mono,monospace", fontSize: 15, fontWeight: 800, color: "#fff" }}>
                      ₹{fmt(filtered.reduce((s, p) => s + parseFloat(p.amount || 0), 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <PaymentModal payment={editPayment}
            onClose={() => this.setState({ showModal: false, editPayment: null })}
            onSaved={() => { this.setState({ showModal: false, editPayment: null }); this.fetch(); }} />
        )}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}
export default PaymentsPage;